import { NgModule } from '@angular/core';
import { DeferLoadingStrategy, provideConfig } from '@spartacus/core';
import { ConsentManagementModule } from '@spartacus/storefront';
import { RestoreCartModule } from './cart/restore-cart/restore-cart.module';
import { CmsLinkModule } from './cms-link/cms-link.module';
import { FooterSocialLinkModule } from './footer-social-link/footer-social-link.module';
import { GgAiTroubleshootModule } from './gg-ai-troubleshoot/gg-ai-troubleshoot.module';
import { GgFirmwareVerificationModule } from './gg-firmware-verification/gg-firmware-verification.module';
import { GgFooterModule } from './gg-footer/gg-footer.module';
import { GgGridModule } from './gg-grid/gg-grid.module';
import { GgMainIntroSliderModule } from './gg-main-intro-slider/gg-main-intro-slider.module';
import { HeaderLinksModule } from './header-links/header-links.module';
import { InteractiveCarouselModule } from './interactive-carousel/interactive-carousel.module';
import { NavigationModule } from './navigation/navigation.module';
import { HisenseProductFacetNavigationModule } from './product-facet-navigation/product-facet-navigation.module';
import { SearchBoxModule } from './search-box/search-box.module';
import { GgSimplePromoBannerModule } from './gg-promo-banner/gg-simple-promo-banner.module';

@NgModule({
  imports: [
    GgMainIntroSliderModule,
    FooterSocialLinkModule,
    GgFooterModule,
    NavigationModule,
    HisenseProductFacetNavigationModule,
    GgGridModule,
    SearchBoxModule,
    CmsLinkModule,
    HeaderLinksModule,
    RestoreCartModule,
    InteractiveCarouselModule,
    GgAiTroubleshootModule,
    ConsentManagementModule,
    GgFirmwareVerificationModule,
    GgSimplePromoBannerModule,
  ],
  providers: [
    provideConfig({
      featureModules: {
        paragraph: {
          module: () => import('./paragraph/paragraph.module').then((m) => m.ParagraphModule),
          cmsComponents: ['CMSParagraphComponent'],
        },
        advanceMarketingForms: {
          module: () =>
            import('./advance-marketing-forms/advance-marketing-forms.module').then(
              (m) => m.AdvanceMarketingFormsModule
            ),
          cmsComponents: ['AdvanceMarketingFormsComponent'],
        },
        cmsImage: {
          module: () => import('./cms-image/cms-image.module').then((m) => m.CmsImageModule),
          cmsComponents: ['CMSImageComponent'],
        },
        gg2And3Columns: {
          module: () => import('./gg-2-and-3-columns/gg-2-and-3-columns.module').then((m) => m.Gg2And3ColumnsModule),
          cmsComponents: ['GG2And3ColumnsComponent', 'GGFeatureItem'],
        },
        ggFlyoutCountrySelect: {
          module: () =>
            import('./gg-flyout-country-select/gg-flyout-country-select.module').then(
              (m) => m.GgFlyoutCountrySelectModule
            ),
          cmsComponents: ['GGFlyoutCountrySelect', 'GGCountry'],
        },
        ggNewsList: {
          module: () => import('./gg-news-list/gg-news-list.module').then((m) => m.GgNewsListModule),
          cmsComponents: ['GGNewsListComponent'],
        },
        ggPromoCategories: {
          module: () =>
            import('./gg-promo-categories/gg-promo-categories.module').then((m) => m.GgPromoCategoriesModule),
          cmsComponents: ['GGPromoCategoriesComponent'],
        },
        ggSubscriber: {
          module: () => import('./gg-subscriber/gg-subscriber.module').then((m) => m.GgSubscriberModule),
          cmsComponents: ['SubscriptionsComponent'],
        },
        ggNewsletterPopup: {
          module: () => import('./gg-newsletter-popup/gg-newsletter-popup.module').then((m) => m.NewsletterModule),
          cmsComponents: ['GGSpaEmailSubscriptionComponent'],
        },
        ggTabbedSlider: {
          module: () => import('./gg-tabbed-slider/gg-tabbed-slider.module').then((m) => m.GgTabbedSliderModule),
          cmsComponents: ['GGTabbedSliderComponent', 'GGImageLinkTabbedSliderComponent'],
        },
        ggTitle: {
          module: () => import('./gg-title/gg-title.module').then((m) => m.GgTitleModule),
          cmsComponents: ['GGTitle'],
        },
        informationSectionCollection: {
          module: () =>
            import('./information-section-collection/information-section-collection.module').then(
              (m) => m.InformationSectionCollectionModule
            ),
          cmsComponents: ['InformationSectionCollectionComponent', 'InformationSectionItemComponent'],
        },
        ggFaqList: {
          module: () => import('./gg-faq-list/gg-faq-list.module').then((m) => m.GgFaqListModule),
          cmsComponents: ['GGFaq', 'GGFaqListComponent'],
        },
        ggDelimiter: {
          module: () => import('./gg-delimiter/gg-delimiter.module').then((m) => m.GgDelimiterModule),
          cmsComponents: ['GGDelimiterComponent'],
        },
        storeFinder: {
          module: () => import('./store-finder/store-finder.module').then((m) => m.StoreFinderModule),
          cmsComponents: ['StoreFinderComponent', 'GGEmbeddedDealerLocatorComponent'],
        },
        orderDetails: {
          module: () => import('./gg-order-details/gg-order-details.module').then((m) => m.GgOrderDetailsModule),
          cmsComponents: [
            'AccountOrderDetailsGroupedItemsComponent',
            'AccountOrderDetailsItemsComponent',
            'ReplenishmentDetailItemsComponent',
          ],
        },

        addressForm: {
          module: () => import('./address-form/address-form.module').then((m) => m.AddressFormModule),
          cmsComponents: ['AddressFormComponent'],
        },
        orderSummary: {
          module: () => import('./order-summary/order-summary.module').then((m) => m.OrderSummaryModule),
          cmsComponents: ['OrderKeyFeaturesComponent'],
        },
        account: {
          module: () => import('./account/account.module').then((m) => m.AccountModule),
          cmsComponents: [
            'AccountSubscriptionsComponent',
            'AccountAddressBookComponent',
            'UpdatePasswordComponent',
            'UpdateProfileComponent',
            'AccountOrderDetailsTotalsComponent',
            'ResetPasswordComponent',
            'RegisterCustomerComponent',
            'ReturningCustomerLoginComponent',
            'UpdateEmailComponent',
            'ForgotPasswordComponent',
            'AccountOrderDetailsOverviewComponent',
            'AccountOrderDetailsSimpleOverviewComponent',
          ],
        },
        listing: {
          module: () => import('./listing/listing.module').then((m) => m.ListingModule),
          cmsComponents: [
            'SearchResultsListComponent',
            'SearchResultsGridComponent',
            'CMSProductListComponent',
            'ProductGridComponent',
            'GGPromoBannerComponent',
            'GGRecommendedProductsComponent',
            'GGRecommendedProductsTabItemsComponent',
            'GGHighlightedProductsComponent',
            'GGHighlightedProductsItemComponent',
          ],
        },
        product: {
          module: () => import('./product/product.module').then((m) => m.ProductModule),
          cmsComponents: [
            'GGDownloadsGridComponent',
            'GGProductDetailsComponent',
            'GGRetailersListComponent',
            'GG100ShoppersRetailersListComponent',
            'GG24TTLRetailersListComponent',
            'GGMikMakRetailersListComponent',
            'GGClick2buyRetailersListComponent',
            'GGGAPRRetailersListComponent',
            'GGHotlineRetailersListComponent',
            'GGInStockReminderActionComponent',
            'GGProductFeatureComponent',
            'GGProductSpecsComponent',
            'ProductImagesComponent',
            'ProductSummaryComponent',
            'ProductIntroComponent',
            'GGProductReviewsComponent',
            'GGISiteRetailersListComponent',
            'GGProductFaqsComponent',
            'BvProductReviewsComponent',
            'KieskeurigProductReviewsComponent',
            'GGEconomicSubjectComponent',
            'GGProductVerificationComponent',
          ],
        },
        bundles: {
          module: () =>
            import('./product/gg-product-bundles/gg-product-bundles.module').then((m) => m.GgProductBundlesModule),
          cmsComponents: ['HisenseProductBundlesComponent'],
        },
        productComparison: {
          module: () => import('./product-comparison/product-comparison.module').then((m) => m.ProductComparisonModule),
          cmsComponents: ['ProductComparisonComponent'],
        },
        cart: {
          module: () => import('./cart/cart.module').then((m) => m.CartModule),
          cmsComponents: ['ProductAddToCartComponent', 'CartTotalsComponent', 'CartComponent'],
        },
        cartShared: {
          module: () => import('./cart-shared/cart-shared.module').then((m) => m.CartSharedModule),
          cmsComponents: [],
        },
        ggCartCoupon: {
          module: () => import('./gg-cart-coupon/gg-cart-coupon.module').then((m) => m.GgCartCouponModule),
          cmsComponents: ['CartApplyCouponComponent'],
        },
        checkout: {
          module: () => import('./checkout/checkout.module').then((m) => m.CheckoutModule),
          cmsComponents: [
            'CheckoutShippingAddress',
            'CheckoutOrderSummary',
            'OrderConfirmationTotalsComponent',
            'GGPaymentMethodComponent',
            'OrderConfirmationItemsComponent',
            'OrderConfirmationOverviewComponent',
            'OrderConfirmationThankMessageComponent',
            'CheckoutPlaceOrder',
            'CheckoutReviewOrder',
            'AccountOrderDetailsOverviewComponent',
          ],
        },
        ggProductSearch: {
          module: () => import('./gg-product-search/gg-product-search.module').then((m) => m.GgProductSearchModule),
          cmsComponents: ['GGProductSearchComponent'],
        },
        ggManualsSearch: {
          module: () => import('./gg-manuals-search/gg-manuals-search.module').then((m) => m.GgManualsSearchModule),
          cmsComponents: ['GGManualsSearchComponent'],
        },
        ggButton: {
          module: () => import('./gg-button/gg-button.module').then((m) => m.GgButtonModule),
          cmsComponents: ['GGButton'],
        },
        ggMediaWithMobileMedia: {
          module: () =>
            import('./gg-media-with-mobile-media/gg-media-with-mobile-media.module').then(
              (m) => m.GgMediaWithMobileMediaModule
            ),
          cmsComponents: [],
        },
        lufcCustomerRegister: {
          module: () =>
            import('./lufc-customer-register/lufc-customer-register.module').then((m) => m.LufcCustomerRegisterModule),
          cmsComponents: ['LUFCCustomerRegisterComponent'],
        },
        anchor: {
          module: () => import('./anchor/anchor.module').then((m) => m.AnchorModule),
          cmsComponents: ['GGAnchor'],
        },
        interactiveCarousel: {
          module: () =>
            import('./interactive-carousel/interactive-carousel.module').then((m) => m.InteractiveCarouselModule),
          cmsComponents: ['InteractiveCarouselComponent'],
        },
        sportMonk: {
          module: () => import('./gg-sportmonk/gg-sportmonk.module').then((m) => m.GgSportmonkModule),
          cmsComponents: [
            'SportMonk',
          ],
        },
        unsubscribeConsent: {
          module: () =>
            import('./gg-subscriber-unsubscribe/gg-subscriber-unsubscribe.module').then(
              (m) => m.GgSubscriberUnsubscribeModule
            ),
          cmsComponents: [
            'SubscriptionUnsubscribeComponent',
          ],
        },
      },
      cmsComponents: {
        AnonymousConsentManagementBannerComponent: {
          component: () =>
            import(
              './gg-cookie-consent/gg-cookie-consent-management-banner/gg-cookie-consent-management-banner.component'
            ).then((m) => m.GgCookieConsentManagementBannerComponent),
          deferLoading: DeferLoadingStrategy.INSTANT,
        },
        UserConsentManagementBannerComponent: {
          component: () =>
            import(
              './gg-cookie-consent/gg-cookie-consent-user-management-banner/gg-cookie-consent-user-management-banner.component'
            ).then((m) => m.GgCookieConsentUserManagementBannerComponent),
          deferLoading: DeferLoadingStrategy.INSTANT,
        },
        UnfoldedSectionComponent: {
          component: () =>
            import('./unfolded-section/unfolded-section.component').then((m) => m.UnfoldedSectionComponent),
        },
        UnfoldedSectionItemComponent: {
          component: () =>
            import('./unfolded-section/unfolded-section-item/unfolded-section-item.component').then(
              (m) => m.UnfoldedSectionItemComponent
            ),
        },
        ScrollToTopComponent: {
          deferLoading: DeferLoadingStrategy.INSTANT,
        },
      },
    }),
  ],
})
export class ComponentsModule {}
